import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Copy, ExternalLink, Calendar, Clock, Share2, Mail, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface ClientPortalLinkManagerProps {
  clientId: string;
  clientName: string;
}

export const ClientPortalLinkManager: React.FC<ClientPortalLinkManagerProps> = ({ 
  clientId, 
  clientName 
}) => {
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [customMessage, setCustomMessage] = useState('');

  // Generate secure invitation link (not session-based)
  const handleGenerateLink = async () => {
    // Use the secure client-portal-invite route which requires authentication
    const portalUrl = `${window.location.origin}/client-portal-invite/${clientId}`;
    setGeneratedLink(portalUrl);
    toast.success('Portal invitation link generated successfully');
  };

  const handleCopyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      toast.success('Link copied to clipboard');
    }
  };

  const handleShareByEmail = async () => {
    if (!generatedLink) return;

    setSendingEmail(true);
    try {
      // Get client email from the database
      const { data: clientData } = await supabase
        .from('clients')
        .select('email, name')
        .eq('id', clientId)
        .single();

      if (!clientData?.email) {
        toast.error('Client email not found. Please ensure the client has an email address.');
        return;
      }

      const { error: emailError } = await supabase.functions.invoke('send-portal-access', {
        body: {
          clientEmail: clientData.email,
          clientName: clientData.name,
          portalUrl: generatedLink,
          plannerName: user?.user_metadata?.display_name || user?.email || 'Your Event Planner',
          message: customMessage.trim() || undefined,
        },
      });

      if (emailError) {
        console.error("Error sending portal email:", emailError);
        throw new Error("Failed to send email");
      }

      toast.success(`Portal access email sent to ${clientData.email}`);
    } catch (error) {
      console.error("Error sending portal access email:", error);
      toast.error('Failed to send email. You can copy the link manually.');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Share2 className="h-4 w-4 mr-2" />
          Generate Portal Link
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Generate Client Portal Invitation</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="flex items-center gap-2 p-3 bg-muted/50 rounded">
            <div className="h-8 w-8 bg-primary/10 rounded-full flex items-center justify-center">
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="font-medium">Client: {clientName}</p>
              <p className="text-sm text-muted-foreground">Generating secure portal invitation</p>
            </div>
          </div>

          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded">
            <Shield className="h-5 w-5 text-green-600" />
            <p className="text-sm text-green-700">
              This link requires the client to sign in with their email address for security.
            </p>
          </div>

          {!generatedLink && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="message">Custom Message (Optional)</Label>
                <Textarea
                  id="message"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Add a personal message to include in the email..."
                  rows={3}
                />
              </div>

              <Button 
                onClick={handleGenerateLink} 
                className="w-full"
              >
                Generate Secure Portal Invitation
              </Button>
            </div>
          )}

          {generatedLink && (
            <div className="space-y-4">
              <Card className="border-green-200 bg-green-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-green-800 flex items-center gap-2">
                    <ExternalLink className="h-5 w-5" />
                    Portal Invitation Generated
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-green-700">Secure Portal URL</Label>
                    <div className="flex gap-2 mt-1">
                      <Input 
                        value={generatedLink} 
                        readOnly 
                        className="font-mono text-sm"
                      />
                      <Button size="icon" variant="outline" onClick={handleCopyLink}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-green-700">
                    <Shield className="h-4 w-4" />
                    <span>Client must authenticate to access the portal</span>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleShareByEmail} className="flex-1" disabled={sendingEmail}>
                      <Mail className="h-4 w-4 mr-2" />
                      {sendingEmail ? 'Sending...' : 'Send Email'}
                    </Button>
                    <Button variant="outline" onClick={() => setGeneratedLink(null)}>
                      Generate New Link
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClientPortalLinkManager;
